import React from 'react';
import styled from 'styled-components';
import { 
  Scale, 
  FileText, 
  Download, 
  ExternalLink,
  ChevronRight,
  Gavel,
  BookOpen,
  ScrollText
} from 'lucide-react';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -3rem;
  position: relative;
  z-index: 2;
`;

const IntroCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-light);
  margin-bottom: 2rem;

  h2 {
    font-family: var(--font-secondary);
    font-size: 1.5rem;
    color: var(--primary-blue);
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    svg {
      color: var(--accent-gold);
    }
  }

  p {
    font-size: 1.05rem;
    color: var(--text-medium);
    line-height: 1.8;
  }
`;

const LegalCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  overflow: hidden;
  margin-bottom: 1.5rem;
  transition: var(--transition);

  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-4px);
  }
`;

const LegalHeader = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  padding: 1.5rem 2rem;
  color: var(--white);
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    width: 40px;
    height: 40px;
    background: rgba(255,255,255,0.2);
    border-radius: 50%;
    padding: 0.5rem;
  }

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.25rem;
  }
`;

const LegalContent = styled.div`
  padding: 2rem;
`;

const LegalTitle = styled.h4`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const LegalDescription = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.8;
  margin-bottom: 1.5rem;
`;

const LegalMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: var(--background-light);
  color: var(--text-dark);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-full);
  font-size: 0.85rem;
  font-weight: 500;

  svg {
    color: var(--primary-blue);
    opacity: 0.7;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-blue);
  color: var(--white);
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  transition: var(--transition);

  &:hover {
    background: var(--accent-gold);
    color: var(--primary-blue-dark);
  }

  svg {
    transition: var(--transition);
  }

  &:hover svg {
    transform: translateY(2px);
  }
`;

const ExternalLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--white);
  color: var(--primary-blue);
  padding: 0.875rem 1.5rem;
  border-radius: var(--radius-md);
  font-weight: 600;
  border: 2px solid var(--border-light);
  transition: var(--transition);

  &:hover {
    border-color: var(--primary-blue);
    background: var(--background-light);
  }
`;

const RelatedSection = styled.div`
  margin-top: 3rem;
`;

const SectionTitle = styled.h3`
  font-family: var(--font-secondary);
  font-size: 1.5rem;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--accent-gold);
  }
`;

const RelatedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RelatedItem = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--border-light);
  transition: var(--transition);

  &:hover {
    border-color: var(--accent-gold);
    transform: translateX(8px);
  }

  .icon {
    width: 48px;
    height: 48px;
    background: var(--background-light);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--primary-blue);
  }

  .content {
    flex: 1;

    .title {
      font-weight: 600;
      color: var(--text-dark);
      margin-bottom: 0.25rem;
    }

    .desc {
      font-size: 0.9rem;
      color: var(--text-light);
    }
  }

  svg:last-child {
    color: var(--text-light);
  }
`;

const TextesJuridiques = () => {
  return (
    (<PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Textes Juridiques</HeroTitle>
          <HeroSubtitle>
            L'ensemble des textes législatifs et réglementaires régissant la Médiature de la République
          </HeroSubtitle>
        </HeroContent>
      </Hero>
      <Container>
        <ContentWrapper>
          <IntroCard>
            <h2><Scale size={24} /> Cadre juridique</h2>
            <p>
              La Médiature de la République du Tchad est régie par un ensemble de textes juridiques 
              qui définissent ses attributions, son organisation et son fonctionnement. Ces textes 
              garantissent l'indépendance et l'efficacité de l'institution dans sa mission de médiation 
              entre l'administration et les citoyens.
            </p>
          </IntroCard>

          <LegalCard>
            <LegalHeader>
              <ScrollText size={24} />
              <h3>Constitution du Tchad</h3>
            </LegalHeader>
            <LegalContent>
              <LegalTitle>
                Constitution du Tchad approuvée par référendum le 17 décembre 2023
              </LegalTitle>
              <LegalDescription>
                La Constitution de la IVème République du Tchad consacre la Médiature de la République 
                comme institution constitutionnelle indépendante. Elle définit les grands principes 
                régissant cette institution et son rôle dans la protection des citoyens face à 
                l'administration publique.
              </LegalDescription>
              <LegalMeta>
                <MetaBadge>
                  <FileText size={14} />
                  Référendum : 17 décembre 2023
                </MetaBadge>
                <MetaBadge>
                  <Gavel size={14} />
                  Loi constitutionnelle N° 001/PC/2025
                </MetaBadge>
                <MetaBadge>
                  <BookOpen size={14} />
                  Décret N°2558/PR/2025
                </MetaBadge>
              </LegalMeta>
              <ActionButtons>
                <DownloadButton href="#" download>
                  <Download size={18} />
                  Télécharger la Constitution
                </DownloadButton>
              </ActionButtons>
            </LegalContent>
          </LegalCard>

          <LegalCard>
            <LegalHeader>
              <Gavel size={24} />
              <h3>Loi</h3>
            </LegalHeader>
            <LegalContent>
              <LegalTitle>
                Loi N°010/PR/2025 du 05 août 2025
              </LegalTitle>
              <LegalDescription>
                Cette loi détermine les attributions, les règles d'organisation, de fonctionnement 
                des services et les modalités de saisine de la Médiature de la République. Elle 
                précise le cadre dans lequel le Médiateur exerce ses missions et les prérogatives 
                dont il dispose pour mener à bien ses investigations.
              </LegalDescription>
              <LegalMeta>
                <MetaBadge>
                  <FileText size={14} />
                  Adoptée le 23 décembre 2025
                </MetaBadge>
                <MetaBadge>
                  <BookOpen size={14} />
                  Publiée le 05 août 2025
                </MetaBadge>
              </LegalMeta>
              <ActionButtons>
                <DownloadButton href="#" download>
                  <Download size={18} />
                  Télécharger la loi
                </DownloadButton>
              </ActionButtons>
            </LegalContent>
          </LegalCard>

          <LegalCard>
            <LegalHeader>
              <BookOpen size={24} />
              <h3>Règlement intérieur</h3>
            </LegalHeader>
            <LegalContent>
              <LegalTitle>
                Règlement intérieur de la Médiature de la République
              </LegalTitle>
              <LegalDescription>
                              Le règlement intérieur précise les modalités pratiques de fonctionnement interne 
                              de l'institution, les procédures de traitement des Réclamations, les relations entre 
                              les différents services et les règles de déontologie applicables aux agents de la Médiature.
                            </LegalDescription>
              <LegalMeta>
                <MetaBadge>
                  <FileText size={14} />
                  En vigueur
                </MetaBadge>
              </LegalMeta>
              <ActionButtons>
                <DownloadButton href="#" download>
                  <Download size={18} />
                  Télécharger le règlement
                </DownloadButton>
              </ActionButtons>
            </LegalContent>
          </LegalCard>


        </ContentWrapper>
      </Container>
    </PageContainer>)
  );
};

export default TextesJuridiques;

